import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Table, Button, Modal, Form, InputGroup, Spinner } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash, FaSearch, FaUsersCog, FaChalkboardTeacher } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';

const initialFormState = {
  name: '',
  description: '',
  organization_id: ''
};

const ManageGroups = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formState, setFormState] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [editingGroup, setEditingGroup] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const normalizeName = useCallback((value) => {
    if (!value || typeof value !== 'string') return '';
    return value.trim();
  }, []);

  const isFetchingRef = useRef(false);

  const fetchGroups = useCallback(async ({ showLoader = true } = {}) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (showLoader) setLoading(true);

    try {
      const response = await api.groups.list();
      if (!response.data?.success) {
        toast.error(response.data?.message || 'Failed to load groups.');
        return;
      }
      setGroups(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      toast.error(`Failed to load groups: ${error.response?.data?.message || error.message}`);
    } finally {
      if (showLoader) setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await api.organizations.getAll();
      const data = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setOrganizations(data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
    fetchOrganizations();
  }, [fetchGroups, fetchOrganizations]);

  const getOrganizationName = useCallback((organizationId) => {
    if (!organizationId) return '-';
    const match = organizations.find((org) => parseInt(org.id) === parseInt(organizationId));
    return match ? match.name : '-';
  }, [organizations]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return groups;
    const term = searchTerm.trim().toLowerCase();
    return groups.filter(group =>
      group.name?.toLowerCase().includes(term) ||
      group.description?.toLowerCase().includes(term)
    );
  }, [groups, searchTerm]);

  const resetForm = useCallback(() => {
    setFormState(initialFormState);
    setFormErrors({});
    setEditingGroup(null);
  }, []);

  const handleShowModal = useCallback((group = null) => {
    if (group) {
      setEditingGroup(group);
      setFormState({
        name: group.name || '',
        description: group.description || '',
        organization_id: group.organization_id ? String(group.organization_id) : ''
      });
    } else {
      resetForm();
    }
    setFormErrors({});
    setShowModal(true);
  }, [resetForm]);

  const handleCloseModal = useCallback(() => {
    if (submitting) return;
    setShowModal(false);
    resetForm();
  }, [resetForm, submitting]);

  const validateForm = useCallback(() => {
    const errors = {};
    const name = normalizeName(formState.name);
    if (!name) errors.name = 'Group name is required';
    else if (name.length > 255) errors.name = 'Group name must be 255 characters or fewer';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formState.name, normalizeName]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      name: normalizeName(formState.name),
      description: formState.description?.trim() || null,
      organization_id: formState.organization_id || null
    };
    setSubmitting(true);

    try {
      if (editingGroup) {
        await api.groups.update(editingGroup.id, payload);
        toast.success('Group updated successfully.');
      } else {
        await api.groups.create(payload);
        toast.success('Group created successfully.');
      }
      await fetchGroups();
      handleCloseModal();
    } catch (error) {
      toast.error(`Failed to save group: ${error.response?.data?.message || error.message}`);
    } finally {
      setSubmitting(false);
    }
  }, [editingGroup, fetchGroups, formState.name, formState.description, formState.organization_id, handleCloseModal, normalizeName, validateForm]);

  const handleAssignClick = useCallback((group) => {
    navigate(`/admin/groups/${group.id}/assign`, { state: { groupName: group.name } });
  }, [navigate]);

  const handleAssignInstructorsClick = useCallback((group) => {
    navigate(`/admin/groups/${group.id}/assign-instructors`, { state: { groupName: group.name } });
  }, [navigate]);

  const handleDeleteClick = useCallback((group) => {
    setGroupToDelete(group);
    setShowDeleteModal(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!groupToDelete) return;
    setDeleteLoading(true);
    try {
      await api.groups.remove(groupToDelete.id);
      toast.success(`Group "${groupToDelete.name}" deleted successfully.`);
      await fetchGroups();
    } catch (error) {
      toast.error(`Failed to delete group: ${error.response?.data?.message || error.message}`);
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
      setGroupToDelete(null);
    }
  }, [fetchGroups, groupToDelete]);

  const handleDeleteCancel = useCallback(() => {
    if (deleteLoading) return;
    setShowDeleteModal(false);
    setGroupToDelete(null);
  }, [deleteLoading]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  }, []);

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="mb-3">
        <h4 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Manage Group
        </h4>
      </div>

      {/* Search + Add */}
      <Row className="mb-3 g-3 align-items-center">
        <Col xs={12} md={6} lg={5}>
          <InputGroup className="shadow-sm rounded-lg sm:rounded-xl overflow-hidden">
            <InputGroup.Text className="bg-white border-0 text-purple-600 px-3">
              <FaSearch className="w-5 h-5" />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-0 focus:ring-2 focus:ring-purple-500 text-sm"
            />
          </InputGroup>
        </Col>
        <Col xs={12} md={6} lg={7} className="text-end">
          <Button
            bsPrefix="custom-hamburger"
            onClick={() => handleShowModal()}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            <FaPlus className="w-5 h-5" />
            <span>Add Group</span>
          </Button>
        </Col>
      </Row>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16">
          <Spinner animation="border" className="w-12 h-12 text-purple-600" />
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="text-center py-12 bg-white/80 backdrop-blur rounded-2xl shadow-lg border border-purple-100">
          <p className="text-lg font-medium text-purple-700">
            {searchTerm ? 'No groups found matching your search.' : 'No groups added yet. Click "Add Group" to get started.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <Table bordered hover className="m-0 min-w-max text-sm">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-3 px-3 text-left font-bold">Group Name</th>
                <th className="py-3 px-3 text-left font-bold">Description</th>
                <th className="py-3 px-3 text-left font-bold">Organization</th>
                <th className="py-3 px-3 text-left font-bold">Created Date</th>
                <th className="py-3 px-3 text-left font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups.map((group) => (
                <tr key={group.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                  <td className="py-3 px-4 font-medium text-gray-800">{group.name}</td>
                  <td className="py-3 px-4 text-gray-600">{group.description || '-'}</td>
                  <td className="py-3 px-4 text-gray-600">{getOrganizationName(group.organization_id)}</td>
                  <td className="py-3 px-4 text-gray-600">{formatDate(group.created_at)}</td>
                  <td className="py-3 px-3 text-left">
                    <div className="d-flex gap-3 justify-content-left">
                      <Button
                        bsPrefix="custom-hamburger"
                        size="sm"
                        onClick={() => handleShowModal(group)}
                        className="border-purple-300 text-purple-600 hover:bg-purple-100 rounded p-1"
                      >
                        <FaEdit className="w-5 h-5" />
                      </Button>
                      <Button
                        bsPrefix="custom-hamburger"
                        size="sm"
                        onClick={() => handleAssignClick(group)}
                        title="Assign / Deassign Users"
                        className="border-blue-300 text-blue-600 hover:bg-blue-50 rounded p-1"
                      >
                        <FaUsersCog className="w-5 h-5" />
                      </Button>
                      <Button
                        bsPrefix="custom-hamburger"
                        size="sm"
                        onClick={() => handleAssignInstructorsClick(group)}
                        title="Assign / Deassign Instructors"
                        className="border-amber-300 text-amber-600 hover:bg-amber-50 rounded p-1"
                      >
                        <FaChalkboardTeacher className="w-5 h-5" />
                      </Button>
                      <Button
                        bsPrefix="custom-hamburger"
                        size="sm"
                        onClick={() => handleDeleteClick(group)}
                        className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                      >
                        <FaTrash className="w-5 h-5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered fullscreen="sm-down">
        <Form onSubmit={handleSubmit} className="p-0 m-0 space-y-6">
          <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4">
            <Modal.Title className="font-bold text-base sm:text-lg">
              {editingGroup ? 'Edit Group' : 'Add New Group'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3 sm:p-5 bg-gray-50">
            <Form.Group className="mb-3">
              <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">
                Group Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formState.name}
                onChange={handleInputChange}
                isInvalid={!!formErrors.name}
                maxLength={255}
                placeholder="e.g., Frontend Team, Sales, Region North"
                disabled={submitting}
                className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group>
              <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">
                Description
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formState.description}
                onChange={handleInputChange}
                placeholder="Optional description for this group"
                disabled={submitting}
                className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">
                Organization <span className="text-muted">(optional)</span>
              </Form.Label>
              <Form.Select
                name="organization_id"
                value={formState.organization_id}
                onChange={handleInputChange}
                disabled={submitting}
                className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
              >
                <option value="">Not linked to an organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="bg-gray-100 border-0 p-3 sm:p-4">
            <Row className="w-100 g-2">
              <Col xs={6}>
                <Button
                  bsPrefix="custom-hamburger"
                  onClick={handleCloseModal}
                  disabled={submitting}
                  className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                >
                  Cancel
                </Button>
              </Col>
              <Col xs={6}>
                <Button
                  bsPrefix="custom-hamburger"
                  type="submit"
                  disabled={submitting}
                  className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                >
                  {submitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {editingGroup ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      {editingGroup ? 'Update' : 'Create'} Group
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered fullscreen="sm-down">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete Group</h5>
          <p className="text-gray-600">
            Are you sure you want to delete{' '}
            <strong>{groupToDelete ? groupToDelete.name : ''}</strong>?
          </p>
          <p className="text-sm text-gray-500">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Group'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageGroups;
